// src/pages/main/components/item/useItem.ts
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useItemInteractions } from "../../../../common/hooks/useItemInteractions";
import {
  CartItem,
  SaleItemInterface,
  StockStore,
} from "../../../../common/types/types";
import {
  addToCart,
  decreaseQuantity,
  increaseQuantity,
  removeFromCart,
} from "../../../../store/slices/cartSlice";
import { RootState } from "../../../../store/store";
import { selectCurrentUser } from "../../../../store/slices/currentUserSlice";
import {
  selectSaleItemById,
  selectIsFavorite,
} from "../../../../store/slices/saleItemListSlice";
import { selectCurrentCluster } from "../../../../store/slices/currentClusterSlice";
import { selectForceStores } from "../../../../store/slices/storeListSlice";
import { ROUTES } from "../../../../common/utils/constants";
import { checkItemAvailability } from "../../../../common/utils/functions/checkItemAvailability";
import { useDisplayPrice } from "../../../../common/hooks/useDisplayPrice";

export const useItem = (item: SaleItemInterface) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toggleFavorite, trackView } = useItemInteractions();
  const currentUser = useSelector(selectCurrentUser);
  const currentCluster = useSelector(selectCurrentCluster);
  const selectedStoreIds = useSelector(selectForceStores);
  const forceStore = currentCluster?.forceStore ?? false;
  const getPrice = useDisplayPrice();

  const updatedItem = useSelector(
    (state: RootState) => selectSaleItemById(state, item.id!) || item,
  );
  const isFavorite = useSelector((state: RootState) =>
    currentUser?.id ? selectIsFavorite(state, item.id!, currentUser.id) : false,
  );
  const totalFavorites = useSelector(
    (state: RootState) =>
      selectSaleItemById(state, item.id!)?.totalFavorites || 0,
  );
  const averageRating = useSelector(
    (state: RootState) => selectSaleItemById(state, item.id!)?.averageRating,
  );
  const totalRatings = useSelector(
    (state: RootState) =>
      selectSaleItemById(state, item.id!)?.totalRatings || 0,
  );
  const totalViews = useSelector(
    (state: RootState) =>
      Number(selectSaleItemById(state, item.id!)?.totalViews) || 0,
  );
  const lastView = useSelector(
    (state: RootState) => selectSaleItemById(state, item.id!)?.views?.[0],
  );

  const cartItem = useSelector((state: RootState) =>
    state.cart.items.find((el: CartItem) => String(el.id) === String(item.id)),
  );

  const quantity = cartItem?.quantity || 0;

  const maxAvailable = useMemo(() => {
    if (!item.useStock) return Infinity;
    // ... остальная логика maxAvailable без изменений
  }, [item, currentCluster]);

  const isIncreaseDisabled = useMemo(() => {
    if (!updatedItem.useStock) return false;
    if (!maxAvailable) return true;
    return quantity >= maxAvailable;
  }, [quantity, maxAvailable, updatedItem.useStock]);

  const availability = useMemo(() => {
    return checkItemAvailability({
      item,
      forceStore,
      selectedStoreIds,
    });
  }, [item, forceStore, selectedStoreIds]);

  const isOutOfStock = !availability.available;

  const handlePress = useCallback(async () => {
    await trackView(item.id!);
    navigate(`${ROUTES.ITEM}/${item.id}`);
  }, [item.id, trackView, navigate]);

  const handleFavoritePress = useCallback(async () => {
    if (currentUser?.id) {
      toggleFavorite(item.id!);
    }
  }, [item.id, currentUser?.id, toggleFavorite]);

  const handleAddToCart = useCallback(() => {
    if (isOutOfStock) return;
    const price = getPrice(item.prices);
    dispatch(
      addToCart({
        id: String(item.id),
        name: item.name,
        price,
        image: item.files?.[0],
        categoryId: Number(item.categoryId),
        minQuantity: item.minQuantity ?? 1,
        quantityStep: item.quantityStep ?? 1,
        attributes: item.attributes || [],
        unit: item.unit,
      }),
    );
  }, [dispatch, item, isOutOfStock, getPrice]);

  const handleIncrease = useCallback(() => {
    if (!isIncreaseDisabled && !isOutOfStock) {
      dispatch(increaseQuantity(String(item.id)));
    }
  }, [dispatch, item.id, isIncreaseDisabled, isOutOfStock]);

  const handleDecrease = useCallback(() => {
    dispatch(decreaseQuantity(String(item.id)));
  }, [dispatch, item.id]);

  const handleRemove = useCallback(() => {
    dispatch(removeFromCart(String(item.id)));
  }, [dispatch, item.id]);

  const renderData = useMemo(() => {
    return {
      averageRating,
      totalRatings,
      totalFavorites,
      totalViews,
      lastView,
      showRating: typeof averageRating === "number" && !isNaN(averageRating),
      showFavorites: totalFavorites > 0,
      showViews: totalViews > 0,
      showLastView: lastView?.viewedAt,
    };
  }, [averageRating, totalRatings, totalFavorites, totalViews, lastView]);

  return {
    updatedItem,
    isFavorite,
    quantity,
    renderData,
    maxAvailable,
    isIncreaseDisabled,
    handlePress,
    handleFavoritePress,
    handleAddToCart,
    handleIncrease,
    handleDecrease,
    handleRemove,
    currentUser,
    totalFavorites,
    isOutOfStock,
    availability,
  };
};
